import React, { Component, createElement, Fragment } from 'react';
import styled from 'styled-components';
import { inject, observer } from 'mobx-react';
import { Button, PrimaryButton } from 'office-ui-fabric-react/lib/Button';

import { websocketsUri } from 'helpers/websockets';
import { localizable } from 'components/Localization';

import ActionSelector from './components/ActionSelector';
import DefaultResults from './components/Results';

import NotificationPreview from 'components/NotificationPreview';

import { getQueryStringValue } from '../../helpers/queryString';
import { when } from 'mobx';

const Confirmation = ({ className, store: { summaryClass, actionStore } }) => (
  <div className={className}>
    {createElement(summaryClass, { store: actionStore })}
  </div>
);

const Results = ({ theme, store, history }) => (
  <div>
    {store.resultsClass &&
      createElement(store.resultsClass, {
        key: 'results',
        theme,
        store,
        history,
      })}
    {!store.resultsClass && (
      <DefaultResults
        key="results"
        theme={theme}
        store={store}
        history={history}
      />
    )}
  </div>
);

const Action = ({
  className,
  store: { currentActionStep, actionStore },
  onValidChanged,
}) => (
  <div className={className}>
    {createElement(currentActionStep, {
      store: actionStore,
      onValidChanged,
    })}
  </div>
);

const Info = ({ store: { currentActionInfo, actionStore } }) => (
  <Fragment>
    {currentActionInfo && (
      <Fragment>
        {createElement(currentActionInfo, {
          store: actionStore,
        })}
      </Fragment>
    )}
  </Fragment>
);

const StyledAction = styled(Action)`
  display: flex;
  flex-flow: row-reverse nowrap;
  box-shadow: 0 0 15 #767676;

  .control {
    flex: 2 2;
    padding-right: 2em;
  }

  .hint {
    flex: 1 1;
    width: 20em;
    box-sizing: border-box;
    padding: 1em 1em 1em 2em;
    border: solid 2px ${(props) => props.theme.palette.themePrimary};
    color: ${(props) => props.theme.palette.themePrimary};
  }

  @media only screen and (max-width: 479px) {
    flex-flow: column nowrap;
    box-shadow: 0;
    .control {
      padding-right: 0;
    }
    .hint {
      border: solid 1px ${(props) => props.theme.palette.themePrimary};
      width: auto;
    }
  }
`;

const Content = styled.div`
  .card {
    max-width: 80em;
    margin: 2em auto;
    padding: 1em 1em 5em 1em;

    box-shadow: rgba(0, 0, 0, 0.4) 0px 0px 5px 0px;
    box-sizing: border-box;

    position: relative;
  }

  .actions {
    position: absolute;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100%;
    padding: 1em;
    display: flex;
    justify-content: space-between;
    border-top: solid 1px #eee;

    .buttons > * {
      margin-left: 1em;
    }
  }

  @media only screen and (max-width: 479px) {
    .card {
      max-width: 479px;
      margin-top: 0;
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px;
      position: initial;
    }
  }
`;

const Breadcrumbs = styled.ul`
  list-style: none outside;
  margin: 1em;
  padding: 0em;

  display: flex;
  flex-flow: row nowrap;
  justify-items: center;
`;

const BreadcrumbItem = styled(({ index, title, ...rest }) => (
  <li {...rest}>
    <span className="index">{index}</span>{' '}
    <span className="title">{title}</span>
  </li>
))`
  color: #767676;

  &:before {
    content: ' > ';
    font-weight: normal;
    display: inline-block;
    text-align: center;
    width: 3em;
  }

  .index {
    border: solid 2px #767676;
    border-radius: 1em;
    margin-right: 0.2em;
    display: inline-block;
    width: 1.8em;
    line-height: 1.8em;
    text-align: center;
    font-weight: bold;
  }

  white-space: nowrap;

  &.selected {
    font-weight: bold;

    .index {
      background: #767676;
      color: #fff;
    }
  }

  &:first-child:before {
    display: none;
  }

  &.selected:first-child ~ li {
    display: none;
  }

  @media only screen and (max-width: 479px) {
    &:before {
      content: '>';
      width: 1.5em;
    }
    .index {
      border: solid 1px #767676;
      border-radius: 1em;
      margin-right: 0.1em;
      display: inline-block;
      width: 1.5em;
      line-height: 1.5em;
    }
    .title {
      display: none;
    }
    &.selected:first-child {
      .title {
        display: initial;
      }
    }
  }
`;

@inject('saleWizard', 'sale', 'user', 'app')
@localizable
@observer
class SaleWizard extends Component {
  componentDidMount() {
    const { sale, saleWizard, match } = this.props;

    if (!sale.connected) {
      const uri = websocketsUri(
        `/events/v1/sale?sid=${encodeURIComponent(match.params.sale)}`
      );
      sale.connect(uri);
    }

    const disposer = when(
      () => sale.connected && sale.saleId,
      () => {
        const action = getQueryStringValue('action');
        if (!!action) {
          saleWizard.selectActionByKey(action);
        }
        disposer();
      }
    );
  }

  renderActionStep() {
    var { currentActionStep, actionStore } = this.props.saleWizard;

    return (
      <div>{createElement(currentActionStep, { store: actionStore })}</div>
    );
  }

  handleBack = () => {
    const { history, saleWizard } = this.props;
    history.push(`/dashboard/${saleWizard.source.saleId}`);
  };

  onValidChanged = (valid) => {
    const { saleWizard } = this.props;
    saleWizard.currentStepValidChanged(valid);
  };

  render() {
    var { t, history, app, sale } = this.props;
    if (!sale.connected) {
      return null; // TODO: spinner?
    }

    var {
      currentAction,
      currentStep,
      isInitialStep,
      isActionStep,
      isConfirmationStep,
      isResultsStep,
      breadcrumbs,
      showPrevious,
      showNext,
      showSubmit,
      handlePrevious,
      handleNext,
      handleSubmit,
    } = this.props.saleWizard;

    var { item } = this.props.saleWizard.source;

    return (
      <Fragment>
        <div className="title">
          <a className="back" onClick={this.handleBack}>
            <i className="ms-Icon ms-Icon--NavigateBack" aria-hidden="true"></i>
            <span>{t('Back')}</span>
          </a>
          <span>
            {isInitialStep ? t('Sale wizard') : t(currentAction + ' title')}
          </span>
          <span className="collapsible">
            {item.number}: {item.title}
          </span>
        </div>

        <Breadcrumbs className="menu">
          {breadcrumbs.map((item, index) => (
            <BreadcrumbItem
              key={item}
              className={index === currentStep ? 'selected' : ''}
              index={index + 1}
              title={t(`${item} title`)}
            />
          ))}
        </Breadcrumbs>

        <Content className="content">
          {isInitialStep ? (
            <ActionSelector
              theme={app.theme}
              key="initial"
              className="ms-fadeIn100"
              store={this.props.saleWizard}
              t={t}
            />
          ) : (
            <div className="card">
              {isConfirmationStep && (
                <Confirmation
                  key="confirm"
                  className="ms-fadeIn100"
                  store={this.props.saleWizard}
                />
              )}
              {isActionStep && (
                <StyledAction
                  theme={app.theme}
                  key="action"
                  className="ms-fadeIn100"
                  store={this.props.saleWizard}
                  onValidChanged={this.onValidChanged}
                />
              )}

              {isResultsStep && (
                <Results
                  theme={app.theme}
                  store={this.props.saleWizard}
                  history={history}
                />
              )}

              <div className="actions">
                <div className="info">
                  {<Info store={this.props.saleWizard} />}
                </div>
                <div className="buttons">
                  {showPrevious && (
                    <Button onClick={handlePrevious}>{t('Previous')}</Button>
                  )}
                  {showNext && (
                    <PrimaryButton onClick={handleNext}>
                      {t('Next')}
                    </PrimaryButton>
                  )}
                  {showSubmit && (
                    <PrimaryButton onClick={handleSubmit}>
                      {t('Submit')}
                    </PrimaryButton>
                  )}
                  {showSubmit && currentAction === 'award' && (
                    <NotificationPreview
                      action={`/api/v1/wizard/${this.props.saleWizard.source.saleId}/award/notifications`}
                      trigger="button"
                      query={{
                        locale: this.props.saleWizard.source.user.claims.locale,
                      }}
                      data={{
                        buyerId:
                          this.props.saleWizard.actionStore.selectedBuyerId,
                        message: this.props.saleWizard.actionStore.message,
                        rejectMessage:
                          this.props.saleWizard.actionStore.rejectMessage,
                      }}
                    />
                  )}
                  {showSubmit && currentAction === 'confirmAward' && (
                    <NotificationPreview
                      action={`/api/v1/wizard/${this.props.saleWizard.source.saleId}/award/confirm/notifications`}
                      trigger="button"
                      query={{
                        locale: this.props.saleWizard.source.user.claims.locale,
                      }}
                      data={{
                        rejectMessage:
                          this.props.saleWizard.actionStore.rejectMessage,
                      }}
                    />
                  )}
                  {showSubmit && currentAction === 'increase' && (
                    <NotificationPreview
                      action={`/api/v1/wizard/${this.props.saleWizard.source.saleId}/increase/notifications`}
                      trigger="button"
                      query={{
                        locale: this.props.saleWizard.source.user.claims.locale,
                      }}
                      data={{
                        message: this.props.saleWizard.actionStore.message,
                        round: {
                          ...this.props.saleWizard.actionStore.roundEditStore
                            .saveableRound,
                          message:
                            this.props.saleWizard.actionStore.buyerMessage,
                        },
                        acceptOffers:
                          this.props.saleWizard.actionStore.acceptedOffers,
                        rejectOffers:
                          this.props.saleWizard.actionStore.rejectedOffers,
                      }}
                    />
                  )}
                  {showSubmit && currentAction === 'rejectAll' && (
                    <NotificationPreview
                      action={`/api/v1/wizard/${this.props.saleWizard.source.saleId}/rejectall/notifications`}
                      trigger="button"
                      query={{
                        locale: this.props.saleWizard.source.user.claims.locale,
                      }}
                      data={{
                        message: this.props.saleWizard.actionStore.message,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </Content>
      </Fragment>
    );
  }
}

export default SaleWizard;



// WEBPACK FOOTER //
// ./src/app/containers/SaleWizard/index.jsx